import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, RefreshCw, Plus, X, Trash2, EyeOff, Eye, ArrowUp, ArrowDown } from "lucide-react";
import {
  useEAs,
  useHiddenEAs,
  useHideEas,
  useGroups,
  useCreateGroup,
  useDeleteGroup,
  useAssignGroup,
  useSetGroupExcluded,
  REFRESH_INTERVAL_MS,
} from "../hooks/useDashboardData";
import { StatusBadge } from "../components/StatusBadge";
import { GroupFilterBar } from "../components/GroupFilterBar";
import { RefreshCountdown } from "../components/RefreshCountdown";
import { TableRowSkeleton, Skeleton } from "../components/Skeleton";
import { formatCurrency, formatNumber, relativeTime } from "../lib/format";
import { filterEasByGroup } from "../lib/groups";
import type { EaStatus, Group } from "../types";

const columns = [
  "Account",
  "EA Name",
  "Broker",
  "Symbol",
  "Status",
  "Group",
  "Today's Profit",
  "Floating",
  "Daily Max Float",
  "Largest Lot",
  "Balance",
  "Equity",
  "Margin Level",
  "Open Trades",
  "Last Update",
];

type SortKey =
  | "today_profit"
  | "floating"
  | "daily_max_float"
  | "largest_lot_today"
  | "balance"
  | "equity"
  | "margin_level"
  | "open_trades"
  | "last_heartbeat";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "today_profit", label: "Today's Profit" },
  { key: "floating", label: "Floating" },
  { key: "daily_max_float", label: "Daily Max Float" },
  { key: "largest_lot_today", label: "Largest Lot" },
  { key: "balance", label: "Balance" },
  { key: "equity", label: "Equity" },
  { key: "margin_level", label: "Margin Level" },
  { key: "open_trades", label: "Open Trades" },
  { key: "last_heartbeat", label: "Last Update" },
];

function sortValue(ea: EaStatus, key: SortKey): number {
  switch (key) {
    case "today_profit":
      return ea.today_profit;
    case "floating":
      return ea.floating_profit - ea.floating_loss;
    case "daily_max_float":
      return ea.daily_max_float;
    case "largest_lot_today":
      return ea.largest_lot_today;
    case "balance":
      return ea.balance;
    case "equity":
      return ea.equity;
    case "margin_level":
      return ea.margin_level;
    case "open_trades":
      return ea.open_trades;
    case "last_heartbeat":
      return ea.last_heartbeat ? new Date(ea.last_heartbeat.replace(" ", "T") + "Z").getTime() : 0;
  }
}

export function EAList() {
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useEAs();
  const { data: groupsData } = useGroups();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [filterGroupId, setFilterGroupId] = useState<number | null>(() => {
    const fromUrl = searchParams.get("groupId");
    return fromUrl ? Number(fromUrl) : null;
  });
  const [sortKey, setSortKey] = useState<SortKey | "">("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [manageOpen, setManageOpen] = useState(false);
  const [hiddenPanelOpen, setHiddenPanelOpen] = useState(false);
  const navigate = useNavigate();

  const groups = groupsData?.groups ?? [];
  const groupNameById = new Map(groups.map((g) => [g.id, g.name]));

  const filteredEas = filterEasByGroup(data?.eas ?? [], filterGroupId, groups).filter((ea) => {
    const q = query.toLowerCase();
    return (
      ea.ea_name.toLowerCase().includes(q) ||
      ea.account_number.toLowerCase().includes(q) ||
      ea.broker.toLowerCase().includes(q) ||
      ea.symbol.toLowerCase().includes(q)
    );
  });

  const eas = sortKey
    ? [...filteredEas].sort(
        (a, b) => (sortValue(a, sortKey) - sortValue(b, sortKey)) * (sortDir === "asc" ? 1 : -1),
      )
    : filteredEas;

  const onlineEas = eas.filter((ea) => ea.status === "online");
  const offlineEas = eas.filter((ea) => ea.status === "offline");

  function toggleOne(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllIn(ids: number[]) {
    setSelected((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  }

  function closeManage() {
    setManageOpen(false);
    setSelected(new Set());
  }

  const selectedEas = eas.filter((ea) => selected.has(ea.id));

  return (
    <div className="space-y-4">
      <GroupFilterBar
        groups={groups}
        value={filterGroupId}
        onChange={setFilterGroupId}
        totalCount={data ? filterEasByGroup(data.eas, null, groups).length : undefined}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search EA, account, broker, symbol..."
              className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-sm text-slate-100 outline-none ring-emerald-500/50 placeholder:text-slate-600 focus:ring-2"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey | "")}
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300 outline-none ring-emerald-500/50 focus:ring-2"
            >
              <option value="">Sort by...</option>
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
            {sortKey && (
              <button
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                className="flex items-center justify-center rounded-lg border border-slate-700 p-2 text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800"
                aria-label={sortDir === "asc" ? "Sort ascending" : "Sort descending"}
                title={sortDir === "asc" ? "Ascending" : "Descending"}
              >
                {sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
          <button
            onClick={() => (manageOpen ? closeManage() : setManageOpen(true))}
            className="rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800"
          >
            Manage Groups
          </button>
          <button
            onClick={() => setHiddenPanelOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800"
          >
            <Eye className="h-3.5 w-3.5" />
            Hidden EAs
          </button>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-sm text-slate-500">{eas.length} EAs</span>
          <RefreshCountdown
            dataUpdatedAt={dataUpdatedAt}
            intervalMs={REFRESH_INTERVAL_MS}
            isFetching={isFetching}
          />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {manageOpen && <GroupManager groups={groups} onClose={closeManage} />}

      {hiddenPanelOpen && <HiddenEasPanel onClose={() => setHiddenPanelOpen(false)} />}

      {selected.size > 0 && (
        <AssignBar
          count={selected.size}
          groups={groups}
          selectedEas={selectedEas}
          onDone={() => setSelected(new Set())}
        />
      )}

      {isLoading ? (
        <>
          <div className="space-y-3 lg:hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <EaCardSkeleton key={i} />
            ))}
          </div>
          <div className="hidden overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60 lg:block">
            <table className="w-full min-w-[1200px] text-left text-sm">
              <tbody className="divide-y divide-slate-800/70">
                {Array.from({ length: 6 }).map((_, i) => (
                  <TableRowSkeleton key={i} columns={columns.length + (manageOpen ? 1 : 0)} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : eas.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-500">
          No EAs found.
        </div>
      ) : (
        <>
          <EaSection
            title="Online"
            eas={onlineEas}
            groupNameById={groupNameById}
            manageOpen={manageOpen}
            selected={selected}
            onToggleOne={toggleOne}
            onToggleAll={() => toggleAllIn(onlineEas.map((ea) => ea.id))}
            onOpen={(id) => navigate(`/eas/${id}`)}
          />
          <EaSection
            title="Offline"
            eas={offlineEas}
            groupNameById={groupNameById}
            manageOpen={manageOpen}
            selected={selected}
            onToggleOne={toggleOne}
            onToggleAll={() => toggleAllIn(offlineEas.map((ea) => ea.id))}
            onOpen={(id) => navigate(`/eas/${id}`)}
          />
        </>
      )}
    </div>
  );
}

function EaSection({
  title,
  eas,
  groupNameById,
  manageOpen,
  selected,
  onToggleOne,
  onToggleAll,
  onOpen,
}: {
  title: string;
  eas: EaStatus[];
  groupNameById: Map<number, string>;
  manageOpen: boolean;
  selected: Set<number>;
  onToggleOne: (id: number) => void;
  onToggleAll: () => void;
  onOpen: (id: number) => void;
}) {
  const allSelected = eas.length > 0 && eas.every((ea) => selected.has(ea.id));

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-slate-400">
        {title} <span className="text-slate-600">({eas.length})</span>
      </h2>

      {eas.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-4 text-sm text-slate-500">
          No {title.toLowerCase()} EAs.
        </div>
      ) : (
        <>
          {/* Small/medium screens: card layout - every field visible, no horizontal scrolling */}
          <div className="space-y-3 lg:hidden">
            {eas.map((ea) => (
              <EaCard
                key={ea.id}
                ea={ea}
                groupName={ea.group_id ? groupNameById.get(ea.group_id) : undefined}
                showCheckbox={manageOpen}
                selected={selected.has(ea.id)}
                onToggleSelect={() => onToggleOne(ea.id)}
                onOpen={() => onOpen(ea.id)}
              />
            ))}
          </div>

          {/* Large screens: full table */}
          <div className="hidden overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60 lg:block">
            <table className="w-full min-w-[1200px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500">
                  {manageOpen && (
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={onToggleAll}
                        className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-emerald-500"
                        aria-label={`Select all ${title.toLowerCase()} EAs`}
                      />
                    </th>
                  )}
                  {columns.map((col) => (
                    <th key={col} className="whitespace-nowrap px-4 py-3 font-medium">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {eas.map((ea) => (
                  <tr
                    key={ea.id}
                    className="cursor-pointer transition-colors hover:bg-slate-800/40"
                    onClick={() => onOpen(ea.id)}
                  >
                    {manageOpen && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(ea.id)}
                          onChange={() => onToggleOne(ea.id)}
                          className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-emerald-500"
                          aria-label={`Select ${ea.ea_name}`}
                        />
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-100">
                      <Link to={`/eas/${ea.id}`} className="hover:underline">
                        {ea.account_name || ea.account_number}
                      </Link>
                      {ea.account_name && <span className="text-slate-500"> ({ea.account_number})</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{ea.ea_name}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{ea.broker}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{ea.symbol}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <StatusBadge status={ea.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                      {ea.group_id ? (groupNameById.get(ea.group_id) ?? "—") : "—"}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-3 tabular-nums ${
                        ea.today_profit >= 0 ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {formatCurrency(ea.today_profit)}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-3 tabular-nums ${
                        ea.floating_profit - ea.floating_loss >= 0 ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {formatCurrency(ea.floating_profit - ea.floating_loss)}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-3 tabular-nums ${
                        ea.daily_max_float < 0 ? "text-rose-400" : "text-slate-300"
                      }`}
                    >
                      {formatCurrency(ea.daily_max_float)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {formatNumber(ea.largest_lot_today)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {formatCurrency(ea.balance)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {formatCurrency(ea.equity)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {formatNumber(ea.margin_level, 1)}%
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {ea.open_trades}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                      {relativeTime(ea.last_heartbeat)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function AssignBar({
  count,
  groups,
  selectedEas,
  onDone,
}: {
  count: number;
  groups: Group[];
  selectedEas: EaStatus[];
  onDone: () => void;
}) {
  const assignGroup = useAssignGroup();
  const createGroup = useCreateGroup();
  const hideEas = useHideEas();
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState("");

  const selectedIds = selectedEas.map((ea) => ea.id);

  function handleSelectChange(value: string) {
    if (value === "__new__") {
      setCreatingNew(true);
      return;
    }
    const groupId = value === "__clear__" ? null : Number(value);
    assignGroup.mutate({ eaIds: selectedIds, groupId }, { onSuccess: onDone });
  }

  async function handleCreateAndAssign(e: FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const result = await createGroup.mutateAsync(name);
    assignGroup.mutate({ eaIds: selectedIds, groupId: result.group.id }, { onSuccess: onDone });
  }

  function handleHide() {
    const onlineCount = selectedEas.filter((ea) => ea.status === "online").length;
    if (onlineCount > 0) {
      const noun = onlineCount === 1 ? "EA is" : "EAs are";
      const confirmed = window.confirm(
        `${onlineCount} selected ${noun} currently online. Hide ${onlineCount === 1 ? "it" : "them"} anyway? You can restore hidden EAs later from "Hidden EAs".`,
      );
      if (!confirmed) return;
    }
    hideEas.mutate({ eaIds: selectedIds, hidden: true }, { onSuccess: onDone });
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-800/60 bg-emerald-950/30 px-4 py-3">
      <span className="text-sm font-medium text-emerald-300">{count} selected</span>

      {creatingNew ? (
        <form onSubmit={handleCreateAndAssign} className="flex items-center gap-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New group name"
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100 outline-none ring-emerald-500/50 placeholder:text-slate-600 focus:ring-2"
          />
          <button
            type="submit"
            disabled={createGroup.isPending || assignGroup.isPending}
            className="rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
          >
            Create &amp; Assign
          </button>
          <button
            type="button"
            onClick={() => setCreatingNew(false)}
            className="text-sm text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
        </form>
      ) : (
        <select
          defaultValue=""
          onChange={(e) => handleSelectChange(e.target.value)}
          disabled={assignGroup.isPending}
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100 outline-none ring-emerald-500/50 focus:ring-2"
        >
          <option value="" disabled>
            Assign to group...
          </option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
          <option value="__new__">+ New group...</option>
          <option value="__clear__">Remove from group</option>
        </select>
      )}

      <button
        onClick={handleHide}
        disabled={hideEas.isPending}
        className="flex items-center gap-1.5 rounded-lg border border-rose-800/60 px-3 py-1.5 text-sm font-medium text-rose-400 transition-colors hover:bg-rose-950/40 disabled:opacity-60"
      >
        <EyeOff className="h-3.5 w-3.5" />
        Hide
      </button>

      <button onClick={onDone} className="ml-auto text-sm text-slate-400 hover:text-slate-200">
        Clear selection
      </button>
    </div>
  );
}

function HiddenEasPanel({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useHiddenEAs(true);
  const hideEas = useHideEas();
  const hidden = data?.eas ?? [];

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-100">Hidden EAs</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
          <X className="h-4 w-4" />
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : hidden.length === 0 ? (
        <p className="text-sm text-slate-500">No hidden EAs.</p>
      ) : (
        <ul className="space-y-1.5">
          {hidden.map((ea) => (
            <li
              key={ea.id}
              className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2 text-sm"
            >
              <span className="text-slate-200">
                {ea.account_name || ea.ea_name}{" "}
                <span className="text-slate-500">
                  ({ea.account_number}, {ea.ea_name})
                </span>
              </span>
              <button
                onClick={() => hideEas.mutate({ eaIds: [ea.id], hidden: false })}
                disabled={hideEas.isPending}
                className="flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 disabled:opacity-60"
              >
                <Eye className="h-3.5 w-3.5" />
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupManager({ groups, onClose }: { groups: Group[]; onClose: () => void }) {
  const createGroup = useCreateGroup();
  const deleteGroup = useDeleteGroup();
  const setExcluded = useSetGroupExcluded();
  const [name, setName] = useState("");

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    createGroup.mutate(trimmed, { onSuccess: () => setName("") });
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-100">Manage Groups</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleCreate} className="mb-3 flex items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New group name"
          className="w-full max-w-xs rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none ring-emerald-500/50 placeholder:text-slate-600 focus:ring-2"
        />
        <button
          type="submit"
          disabled={createGroup.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
        >
          <Plus className="h-3.5 w-3.5" />
          Create
        </button>
      </form>

      {groups.length === 0 ? (
        <p className="text-sm text-slate-500">No groups yet.</p>
      ) : (
        <>
          <ul className="space-y-1.5">
            {groups.map((g) => (
              <li
                key={g.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 px-3 py-2 text-sm"
              >
                <span className="text-slate-200">
                  {g.name} <span className="text-slate-500">({g.ea_count} EAs)</span>
                </span>
                <div className="flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200">
                    <input
                      type="checkbox"
                      checked={Boolean(g.excluded_from_all)}
                      onChange={(e) =>
                        setExcluded.mutate({ id: g.id, excludedFromAll: e.target.checked })
                      }
                      disabled={setExcluded.isPending}
                      className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-900 accent-emerald-500"
                    />
                    Exclude from "All"
                  </label>
                  <button
                    onClick={() => deleteGroup.mutate(g.id)}
                    disabled={deleteGroup.isPending}
                    className="text-slate-500 hover:text-rose-400"
                    aria-label={`Delete group ${g.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {/* The checkbox above is driven purely by server state, so without this a rejected
              request just silently snaps it back with no explanation. */}
          {setExcluded.isError && (
            <p className="mt-3 text-xs text-rose-400">
              Couldn't update that group: {(setExcluded.error as Error).message}
            </p>
          )}
          <p className="mt-3 text-xs text-slate-500">
            Excluded groups (e.g. demo accounts) stay fully visible under their own tab, but
            their profit and counts are left out of the "All" totals.
          </p>
        </>
      )}
    </div>
  );
}

function EaCard({
  ea,
  groupName,
  showCheckbox,
  selected,
  onToggleSelect,
  onOpen,
}: {
  ea: EaStatus;
  groupName?: string;
  showCheckbox: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 transition-colors hover:border-slate-700 hover:bg-slate-800/40">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {showCheckbox && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 accent-emerald-500"
              aria-label={`Select ${ea.ea_name}`}
            />
          )}
          <button onClick={onOpen} className="min-w-0 text-left">
            <p className="truncate font-medium text-slate-100">{ea.account_name || ea.ea_name}</p>
            <p className="truncate text-xs text-slate-500">
              {ea.account_number} ({ea.ea_name}) &middot; {ea.broker} &middot; {ea.symbol}
              {groupName ? ` · ${groupName}` : ""}
            </p>
          </button>
        </div>
        <StatusBadge status={ea.status} />
      </div>

      <button onClick={onOpen} className="block w-full text-left">
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-slate-800/70 pt-3 sm:grid-cols-3">
          <Field label="Today's Profit" value={formatCurrency(ea.today_profit)} tone={ea.today_profit >= 0 ? "positive" : "negative"} />
          <Field
            label="Floating"
            value={formatCurrency(ea.floating_profit - ea.floating_loss)}
            tone={ea.floating_profit - ea.floating_loss >= 0 ? "positive" : "negative"}
          />
          <Field
            label="Daily Max Float"
            value={formatCurrency(ea.daily_max_float)}
            tone={ea.daily_max_float < 0 ? "negative" : "default"}
          />
          <Field label="Largest Lot" value={formatNumber(ea.largest_lot_today)} />
          <Field label="Balance" value={formatCurrency(ea.balance)} />
          <Field label="Equity" value={formatCurrency(ea.equity)} />
          <Field label="Margin Level" value={`${formatNumber(ea.margin_level, 1)}%`} />
          <Field label="Open Trades" value={String(ea.open_trades)} />
          <Field label="Last Update" value={relativeTime(ea.last_heartbeat)} />
        </div>
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "positive" | "negative";
}) {
  const toneClass =
    tone === "positive" ? "text-emerald-400" : tone === "negative" ? "text-rose-400" : "text-slate-200";
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`truncate text-sm font-medium tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

function EaCardSkeleton() {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    </div>
  );
}
